// src/components/SolicitudRemodelacionModal.js
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import {
    FaArrowLeft,
    FaArrowRight,
    FaCheckCircle,
    FaCloudUploadAlt,
    FaTimes,
} from 'react-icons/fa';
import {
    Timestamp,
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    query,
    serverTimestamp,
    setDoc,
    where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import './SolicitudMudanzaModal.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

const STEP_TITLES = [
    'Información del Solicitante',
    'Detalles de los Trabajos',
    'Documentos requeridos',
    'Confirmar solicitud',
];

const RESPONSABLE_OPTIONS = [
    { value: '', label: 'Selecciona' },
    { value: 'propietario', label: 'Propietario' },
    { value: 'contratista', label: 'Contratista' },
    { value: 'ambos', label: 'Propietario y contratista' },
];

const TRABAJO_OPTIONS = [
    { value: '', label: 'Selecciona' },
    { value: 'pintura', label: 'Pintura' },
    { value: 'cambio_pisos', label: 'Cambio de pisos' },
    { value: 'ventanas_puertas', label: 'Ventanas / Puertas' },
    { value: 'impermeabilizacion', label: 'Impermeabilización' },
    { value: 'remodelacion_cocina_bano', label: 'Remodelación cocina / baño' },
    { value: 'modificacion_fachada', label: 'Modificación de fachada' },
    { value: 'otros', label: 'Otros' },
];

const RUIDO_OPTIONS = [
    { value: '', label: 'Selecciona' },
    { value: 'no', label: 'No' },
    { value: 'poco', label: 'Poco' },
    { value: 'moderado', label: 'Moderado' },
    { value: 'alto', label: 'Alto' },
];

const TRABAJO_LABEL_BY_VALUE = TRABAJO_OPTIONS.reduce((acc, item) => {
    if (item.value) {
        acc[item.value] = item.label;
    }
    return acc;
}, {});

const RESPONSABLE_LABEL_BY_VALUE = RESPONSABLE_OPTIONS.reduce((acc, item) => {
    if (item.value) {
        acc[item.value] = item.label;
    }
    return acc;
}, {});

const RUIDO_LABEL_BY_VALUE = RUIDO_OPTIONS.reduce((acc, item) => {
    if (item.value) {
        acc[item.value] = item.label;
    }
    return acc;
}, {});

const DEFAULT_ALLOWED_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const DAY_KEY_BY_INDEX = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_LABEL = {
    mon: 'Lunes',
    tue: 'Martes',
    wed: 'Miércoles',
    thu: 'Jueves',
    fri: 'Viernes',
    sat: 'Sábado',
    sun: 'Domingo',
};

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const DEFAULT_SCHEDULE = {
    diasPermitidos: ['mon', 'tue', 'wed', 'thu', 'fri'],
    horaInicio: '08:00',
    horaFin: '18:00',
};

const formatTimeLabel = (timeValue) => {
    if (typeof timeValue !== 'string' || !TIME_PATTERN.test(timeValue)) {
        return '';
    }
    const [hoursRaw, minutes] = timeValue.split(':');
    const hours = Number(hoursRaw);
    if (Number.isNaN(hours)) {
        return '';
    }
    const suffix = hours >= 12 ? 'p.m.' : 'a.m.';
    const normalizedHour = ((hours + 11) % 12) + 1;
    const paddedHour = `${normalizedHour}`.padStart(2, '0');
    return `${paddedHour}:${minutes} ${suffix}`;
};

const generateTimeSlots = (start, end, intervalMinutes = 60) => {
    const slots = [];
    if (!start || !end) {
        return slots;
    }
    const interval = Number(intervalMinutes) > 0 ? Number(intervalMinutes) : 60;
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);
    if ([startHour, startMinute, endHour, endMinute].some(Number.isNaN)) {
        return slots;
    }
    const base = new Date();
    base.setSeconds(0, 0);
    base.setMilliseconds(0);
    const current = new Date(base);
    current.setHours(startHour, startMinute, 0, 0);
    const limit = new Date(base);
    limit.setHours(endHour, endMinute, 0, 0);
    let guard = 0;
    while (current <= limit && guard < 2880) {
        const hours = `${current.getHours()}`.padStart(2, '0');
        const minutes = `${current.getMinutes()}`.padStart(2, '0');
        slots.push(`${hours}:${minutes}`);
        current.setMinutes(current.getMinutes() + interval);
        guard += 1;
    }
    return slots;
};

const sanitizeScheduleConfig = (rawConfig) => {
    const source = rawConfig && typeof rawConfig === 'object' ? rawConfig : {};
    const horaInicio = typeof source.horaInicio === 'string' && TIME_PATTERN.test(source.horaInicio)
        ? source.horaInicio
        : DEFAULT_SCHEDULE.horaInicio;
    const horaFin = typeof source.horaFin === 'string' && TIME_PATTERN.test(source.horaFin)
        ? source.horaFin
        : DEFAULT_SCHEDULE.horaFin;
    return {
        diasPermitidos: DEFAULT_SCHEDULE.diasPermitidos,
        horaInicio,
        horaFin,
    };
};

const pickFirstString = (values = []) => {
    for (const raw of values) {
        if (raw == null) {
            continue;
        }
        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed.length > 0) {
                return trimmed;
            }
        } else if (typeof raw === 'number') {
            const numeric = String(raw);
            if (numeric.trim().length > 0) {
                return numeric.trim();
            }
        }
    }
    return '';
};

const buildCasaCandidates = (userData) => {
    if (!userData || typeof userData !== 'object') {
        return [];
    }
    const rawCandidates = [
        userData.numeroCasa,
        userData.numeroLote,
        userData.numero,
        userData.lote,
        userData.houseLabel,
        userData.houseNumber,
        userData.loteNumero,
        userData.loteId,
        userData.casaNumero,
        userData.casaId,
        userData.identificadorCasa,
        userData.houseId,
        userData.casaCodigo,
        userData?.casa?.numero,
        userData?.casaAsignada?.numero,
        userData?.direccion?.numero,
    ];
    const seen = new Set();
    const result = [];
    rawCandidates.forEach((raw) => {
        if (raw == null) {
            return;
        }
        const candidate = typeof raw === 'number' ? String(raw) : String(raw || '').trim();
        if (candidate && !seen.has(candidate)) {
            seen.add(candidate);
            result.push(candidate);
        }
        if (candidate) {
            const compact = candidate.replace(/\s+/g, '');
            if (compact && !seen.has(compact)) {
                seen.add(compact);
                result.push(compact);
            }
            const match = candidate.match(/(\d{1,5}[\w-]*)/);
            if (match && match[1]) {
                const extracted = match[1];
                if (!seen.has(extracted)) {
                    seen.add(extracted);
                    result.push(extracted);
                }
            }
        }
    });
    return result;
};

const buildInitialForm = (userData = null) => ({
    nombreCompleto: pickFirstString([
        userData?.nombreCompleto,
        `${userData?.nombre || ''} ${userData?.apellidos || ''}`.trim(),
        userData?.displayName,
    ]),
    lote: pickFirstString([
        userData?.numeroCasa,
        userData?.numeroLote,
        userData?.lote,
        userData?.houseLabel,
    ]),
    telefonoPrincipal: pickFirstString([
        userData?.telefono,
        userData?.telefonoPrincipal,
        userData?.celular,
        userData?.phoneNumber,
    ]),
    email: pickFirstString([
        userData?.correo,
        userData?.email,
        userData?.emailPrincipal,
    ]).toLowerCase(),
    responsableTrabajo: '',
    nombreContratista: '',
    tipoTrabajo: '',
    descripcionTrabajos: '',
    fechaInicioTrabajo: '',
    duracionDias: '',
    generaraRuido: '',
    horarioLlegada: '',
    aceptaCondiciones: false,
});

const defaultFiles = {
    identificacion: null,
    fotografias: null,
    boceto: null,
};

const uploadSolicitudFile = async (fraccionamientoId, userId, solicitudId, slotKey, file) => {
    if (!file) {
        return null;
    }
    const safeName = file.name ? file.name.replace(/[^\w.-]/g, '_') : `${slotKey}-${Date.now()}`;
    const storageRef = ref(storage, `fraccionamientos/${fraccionamientoId}/solicitudes/${solicitudId}/${slotKey}-${safeName}`);
    const snapshot = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(snapshot.ref);
    return {
        url,
        name: file.name,
        size: file.size,
        type: file.type,
    };
};

const formatDateForSummary = (raw) => {
    if (!raw) {
        return '-';
    }
    try {
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }
        return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (error) {
        return '-';
    }
};

const SolicitudRemodelacionModal = ({
    fraccionamientoId,
    currentUser,
    userData = null,
    onClose,
    onSuccess = null,
}) => {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(() => buildInitialForm(userData));
    const [files, setFiles] = useState(defaultFiles);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [allowedDays, setAllowedDays] = useState(DEFAULT_ALLOWED_DAYS);
    const [residentCasaData, setResidentCasaData] = useState(null);
    const [loadingCasaData, setLoadingCasaData] = useState(false);
    const [scheduleConfig, setScheduleConfig] = useState(() => sanitizeScheduleConfig(DEFAULT_SCHEDULE));

    const documentCount = useMemo(() => 2 + (files.boceto ? 1 : 0), [files.boceto]);

    const trabajoLabel = useMemo(() => TRABAJO_LABEL_BY_VALUE[form.tipoTrabajo] || 'Por definir', [form.tipoTrabajo]);
    const responsableLabel = useMemo(() => RESPONSABLE_LABEL_BY_VALUE[form.responsableTrabajo] || 'Por definir', [form.responsableTrabajo]);
    const ruidoLabel = useMemo(() => RUIDO_LABEL_BY_VALUE[form.generaraRuido] || 'Por definir', [form.generaraRuido]);

    const casaCandidates = useMemo(() => buildCasaCandidates(userData), [userData]);
    const uidCandidates = useMemo(() => [
        currentUser?.uid,
        userData?.uid,
        userData?.userId,
        userData?.usuarioId,
    ].filter((value) => typeof value === 'string' && value.trim().length > 0), [currentUser?.uid, userData?.uid, userData?.userId, userData?.usuarioId]);

    useEffect(() => {
        const shouldSkipCasaLookup = !fraccionamientoId
            || (((!Array.isArray(casaCandidates) || casaCandidates.length === 0)
                && uidCandidates.length === 0));

        if (shouldSkipCasaLookup) {
            setResidentCasaData(null);
            setLoadingCasaData(false);
            return undefined;
        }

        let cancelled = false;
        const fetchCasaData = async () => {
            setLoadingCasaData(true);
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const tryAssignCasaFromDocRef = async (docRef) => {
                    if (cancelled) {
                        return false;
                    }
                    const snap = await getDoc(docRef);
                    if (snap?.exists()) {
                        setResidentCasaData({ id: snap.id, ...snap.data() });
                        return true;
                    }
                    return false;
                };

                for (const candidate of casaCandidates) {
                    const normalized = typeof candidate === 'string' ? candidate.trim() : String(candidate || '').trim();
                    if (!normalized) {
                        continue;
                    }
                    try {
                        const directDocRef = doc(casasRef, normalized);
                        const directMatch = await tryAssignCasaFromDocRef(directDocRef);
                        if (directMatch) {
                            return;
                        }

                        const snapshot = await getDocs(query(casasRef, where('numero', '==', normalized), limit(1)));
                        if (!snapshot.empty) {
                            if (!cancelled) {
                                const docSnap = snapshot.docs[0];
                                setResidentCasaData({ id: docSnap.id, ...docSnap.data() });
                            }
                            return;
                        }
                    } catch (innerError) {
                        console.error('SolicitudRemodelacionModal: error buscando casa por número', normalized, innerError);
                    }
                }

                const uidFields = ['uid', 'userId', 'usuarioId', 'residenteUid', 'residenteId', 'propietarioUid', 'propietarioId'];
                for (const field of uidFields) {
                    for (const uid of uidCandidates) {
                        const normalizedUid = uid.trim();
                        try {
                            const snapshot = await getDocs(query(casasRef, where(field, '==', normalizedUid), limit(1)));
                            if (!snapshot.empty) {
                                if (!cancelled) {
                                    const docSnap = snapshot.docs[0];
                                    setResidentCasaData({ id: docSnap.id, ...docSnap.data() });
                                }
                                return;
                            }
                        } catch (innerError) {
                            console.error(`SolicitudRemodelacionModal: error buscando casa por ${field}`, normalizedUid, innerError);
                        }
                    }
                }

                if (!cancelled) {
                    setResidentCasaData(null);
                }
            } catch (fetchError) {
                console.error('SolicitudRemodelacionModal: no se pudo obtener información de la casa', fetchError);
                if (!cancelled) {
                    setResidentCasaData(null);
                }
            } finally {
                if (!cancelled) {
                    setLoadingCasaData(false);
                }
            }
        };

        fetchCasaData();

        return () => {
            cancelled = true;
        };
    }, [casaCandidates, fraccionamientoId, uidCandidates]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setScheduleConfig(sanitizeScheduleConfig(DEFAULT_SCHEDULE));
            return () => {};
        }

        const configRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'permisosEstructurales');
        const unsubscribe = onSnapshot(
            configRef,
            (snapshot) => {
                if (!snapshot.exists()) {
                    setScheduleConfig(sanitizeScheduleConfig(DEFAULT_SCHEDULE));
                    return;
                }
                setScheduleConfig(sanitizeScheduleConfig(snapshot.data() || {}));
            },
            (listenerError) => {
                console.error('SolicitudRemodelacionModal: no se pudo obtener configuración', listenerError);
                setScheduleConfig(sanitizeScheduleConfig(DEFAULT_SCHEDULE));
            },
        );

        return unsubscribe;
    }, [fraccionamientoId]);

    const horarioSlots = useMemo(() => {
        const interval = scheduleConfig.intervaloMinutos || 60;
        return generateTimeSlots(scheduleConfig.horaInicio, scheduleConfig.horaFin, interval);
    }, [scheduleConfig.horaInicio, scheduleConfig.horaFin, scheduleConfig.intervaloMinutos]);

    const defaultNombre = useMemo(() => pickFirstString([
        residentCasaData?.nombre_residente,
        residentCasaData?.nombre,
        residentCasaData?.titular,
        userData?.nombreCompleto,
        `${userData?.nombre || ''} ${userData?.apellidos || ''}`.trim(),
        userData?.displayName,
    ]), [residentCasaData, userData]);

    const defaultTelefono = useMemo(() => pickFirstString([
        residentCasaData?.telefono,
        residentCasaData?.telefono_principal,
        userData?.telefono,
        userData?.telefonoPrincipal,
        userData?.celular,
        userData?.phoneNumber,
    ]), [residentCasaData, userData]);

    const defaultEmail = useMemo(() => pickFirstString([
        residentCasaData?.email,
        userData?.correo,
        userData?.email,
        userData?.emailPrincipal,
    ]), [residentCasaData, userData]);

    const defaultLote = useMemo(() => pickFirstString([
        residentCasaData?.numero,
        residentCasaData?.numeroCasa,
        residentCasaData?.lote,
        userData?.numeroCasa,
        userData?.numeroLote,
        userData?.lote,
        userData?.houseLabel,
    ]), [residentCasaData, userData]);

    useEffect(() => {
        console.log('SolicitudRemodelacionModal - residentCasaData:', residentCasaData);
        console.log('SolicitudRemodelacionModal - valores calculados:', {
            defaultNombre,
            defaultTelefono,
            defaultEmail,
            defaultLote,
        });
        
        setForm((prev) => ({
            ...prev,
            nombreCompleto: defaultNombre || prev.nombreCompleto,
            telefonoPrincipal: defaultTelefono || prev.telefonoPrincipal,
            email: (defaultEmail || prev.email).toLowerCase(),
            lote: defaultLote || prev.lote,
        }));
    }, [residentCasaData, defaultNombre, defaultTelefono, defaultEmail, defaultLote]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setAllowedDays(DEFAULT_ALLOWED_DAYS);
            return () => {};
        }

        const configRef = doc(db, 'fraccionamientos', fraccionamientoId, 'configuraciones', 'permisosEstructurales');
        const unsubscribe = onSnapshot(configRef, (snapshot) => {
            if (!snapshot.exists()) {
                setAllowedDays(DEFAULT_ALLOWED_DAYS);
                return;
            }

            const data = snapshot.data() || {};
            const categorias = (data.categorias && typeof data.categorias === 'object') ? data.categorias : data;
            const remodelConfig = categorias?.remodelacion || {};
            const rawDays = Array.isArray(remodelConfig.diasPermitidos) ? remodelConfig.diasPermitidos : [];
            const normalized = rawDays
                .map((day) => (typeof day === 'string' ? day.toLowerCase().trim() : ''))
                .filter((day) => DAY_LABEL[day]);
            setAllowedDays(normalized.length ? normalized : DEFAULT_ALLOWED_DAYS);
        }, (listenerError) => {
            console.error('SolicitudRemodelacionModal: no se pudo obtener configuración de permisos estructurales', listenerError);
            setAllowedDays(DEFAULT_ALLOWED_DAYS);
        });

        return unsubscribe;
    }, [fraccionamientoId]);

    const isDayAllowed = useCallback((dateString) => {
        if (!dateString) {
            return true;
        }
        const date = new Date(`${dateString}T00:00:00`);
        if (Number.isNaN(date.getTime())) {
            return false;
        }
        const key = DAY_KEY_BY_INDEX[date.getDay()];
        if (!key) {
            return false;
        }
        return allowedDays.includes(key);
    }, [allowedDays]);

    useEffect(() => {
        if (!form.fechaInicioTrabajo) {
            return;
        }
        if (!isDayAllowed(form.fechaInicioTrabajo)) {
            setForm((prev) => ({ ...prev, fechaInicioTrabajo: '' }));
            setError('La fecha seleccionada no está disponible para remodelaciones según la configuración de administración.');
        }
    }, [form.fechaInicioTrabajo, isDayAllowed]);

    const allowedDaySummary = useMemo(() => {
        if (!allowedDays.length) {
            return '';
        }
        return allowedDays.map((day) => DAY_LABEL[day] || day).join(', ');
    }, [allowedDays]);

    const handleInputChange = useCallback((event) => {
        const { name, value, type, checked } = event.target;

        if (name === 'fechaInicioTrabajo' && value && !isDayAllowed(value)) {
            setError('La fecha seleccionada no está disponible para remodelaciones según la configuración de administración.');
            return;
        }

        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (name === 'fechaInicioTrabajo') {
            setError('');
        }
    }, [isDayAllowed]);

    const handleFileSelect = useCallback((event, key) => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            return;
        }
        if (!ACCEPTED_TYPES.includes(file.type)) {
            setError('Solo se permiten archivos PDF, JPG o PNG.');
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setError('Cada archivo debe pesar menos de 10 MB.');
            return;
        }
        setError('');
        setFiles((prev) => ({ ...prev, [key]: file }));
    }, []);

    const handleRemoveFile = useCallback((key) => {
        setFiles((prev) => ({ ...prev, [key]: null }));
    }, []);

    const validateStep = useCallback((currentStep) => {
        if (currentStep === 0) {
            if (!form.nombreCompleto.trim()) {
                setError('Ingresa el nombre completo del solicitante.');
                return false;
            }
            if (!form.lote.trim()) {
                setError('Indica el número de lote.');
                return false;
            }
            if (!form.telefonoPrincipal.trim()) {
                setError('Proporciona un teléfono de contacto.');
                return false;
            }
            if (!form.email.trim()) {
                setError('Proporciona un correo electrónico.');
                return false;
            }
            if (!form.responsableTrabajo) {
                setError('Selecciona quién realizará los trabajos.');
                return false;
            }
            if ((form.responsableTrabajo === 'contratista' || form.responsableTrabajo === 'ambos') && !form.nombreContratista.trim()) {
                setError('Indica el nombre del contratista.');
                return false;
            }
            setError('');
            return true;
        }
        if (currentStep === 1) {
            if (!form.tipoTrabajo) {
                setError('Selecciona el tipo de trabajo.');
                return false;
            }
            if (!form.descripcionTrabajos.trim()) {
                setError('Describe los trabajos a realizar.');
                return false;
            }
            if (!form.fechaInicioTrabajo) {
                setError('Selecciona la fecha de inicio.');
                return false;
            }
            if (!isDayAllowed(form.fechaInicioTrabajo)) {
                setError('La fecha seleccionada no está disponible para remodelaciones según la configuración de administración.');
                return false;
            }
            const duration = Number.parseInt(form.duracionDias, 10);
            if (Number.isNaN(duration) || duration <= 0) {
                setError('Ingresa la duración estimada en días.');
                return false;
            }
            if (!form.generaraRuido) {
                setError('Indica si los trabajos generarán ruido.');
                return false;
            }
            if (!form.horarioLlegada) {
                setError('Selecciona el horario de llegada.');
                return false;
            }
            setError('');
            return true;
        }
        if (currentStep === 2) {
            if (!files.identificacion) {
                setError('Adjunta la identificación oficial.');
                return false;
            }
            if (!files.fotografias) {
                setError('Adjunta fotografías del área.');
                return false;
            }
            setError('');
            return true;
        }
        if (currentStep === 3) {
            if (!form.aceptaCondiciones) {
                setError('Debes aceptar las condiciones para continuar.');
                return false;
            }
            setError('');
            return true;
        }
        return true;
    }, [files.fotografias, files.identificacion, form.aceptaCondiciones, form.descripcionTrabajos, form.duracionDias, form.email, form.fechaInicioTrabajo, form.generaraRuido, form.lote, form.nombreCompleto, form.nombreContratista, form.responsableTrabajo, form.telefonoPrincipal, form.tipoTrabajo, isDayAllowed]);

    const handleNext = useCallback(() => {
        if (!validateStep(step)) {
            return;
        }
        setStep((prev) => Math.min(prev + 1, STEP_TITLES.length - 1));
    }, [step, validateStep]);

    const handlePrev = useCallback(() => {
        setStep((prev) => Math.max(prev - 1, 0));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!validateStep(3)) {
            return;
        }
        if (!fraccionamientoId || !currentUser?.uid) {
            setError('No pudimos validar tu sesión. Intenta nuevamente.');
            return;
        }
        try {
            setSaving(true);
            setError('');

            const startDate = new Date(`${form.fechaInicioTrabajo}T00:00:00`);
            if (Number.isNaN(startDate.getTime())) {
                throw new Error('Fecha de inicio inválida');
            }
            if (!isDayAllowed(form.fechaInicioTrabajo)) {
                setError('La fecha seleccionada no está disponible para remodelaciones según la configuración de administración.');
                setSaving(false);
                return;
            }
            const startTimestamp = Timestamp.fromDate(startDate);
            const duration = Number.parseInt(form.duracionDias, 10);
            if (Number.isNaN(duration) || duration <= 0) {
                throw new Error('Duración inválida');
            }

            const documentosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
            const solicitudRef = doc(documentosRef);

            const [identInfo, fotosInfo, bocetoInfo] = await Promise.all([
                uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'identificacion', files.identificacion),
                uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'fotografias', files.fotografias),
                files.boceto
                    ? uploadSolicitudFile(fraccionamientoId, currentUser.uid, solicitudRef.id, 'boceto', files.boceto)
                    : Promise.resolve(null),
            ]);

            const payload = {
                segmento: 'solicitudes',
                titulo: 'Remodelación / Modificación',
                descripcion: `Solicitud de remodelación para ${form.lote || 'lote sin especificar'}`,
                tipoSolicitud: 'remodelacion',
                fraccionamientoId,
                createdAt: serverTimestamp(),
                uploadedBy: currentUser.uid,
                uploaderNombre: (form.nombreCompleto || currentUser.displayName || '').trim(),
                uploaderRol: typeof userData?.rol === 'string' && userData.rol.trim().length ? userData.rol.trim().toLowerCase() : 'residente',
                estado: 'pendiente',
                nombreCompleto: form.nombreCompleto.trim(),
                lote: form.lote.trim(),
                telefonoPrincipal: form.telefonoPrincipal.trim(),
                email: form.email.trim().toLowerCase(),
                responsableTrabajo: form.responsableTrabajo,
                tipoTrabajo: form.tipoTrabajo,
                descripcionTrabajos: form.descripcionTrabajos.trim(),
                fechaInicioTrabajo: startTimestamp,
                duracionDias: duration,
                generaraRuido: form.generaraRuido,
                horarioLlegada: form.horarioLlegada.trim(),
                aceptaCondiciones: true,
                documentosAdjuntos: bocetoInfo ? 3 : 2,
                docIdentificacionUrl: identInfo.url,
                docIdentificacionName: identInfo.name,
                docIdentificacionSize: identInfo.size,
                docIdentificacionType: identInfo.type,
                docFotografiasUrl: fotosInfo.url,
                docFotografiasName: fotosInfo.name,
                docFotografiasSize: fotosInfo.size,
                docFotografiasType: fotosInfo.type,
            };

            if (form.nombreContratista.trim()) {
                payload.nombreContratista = form.nombreContratista.trim();
            }
            if (bocetoInfo) {
                payload.docBocetoUrl = bocetoInfo.url;
                payload.docBocetoName = bocetoInfo.name;
                payload.docBocetoSize = bocetoInfo.size;
                payload.docBocetoType = bocetoInfo.type;
            }

            await setDoc(solicitudRef, payload);

            if (onSuccess) {
                onSuccess('Solicitud de remodelación enviada. Te avisaremos cuando la administración la revise.');
            }
        } catch (submitError) {
            console.error('SolicitudRemodelacionModal: no se pudo registrar la solicitud', submitError);
            setError('No pudimos registrar la solicitud. Intenta nuevamente.');
            setSaving(false);
            return;
        }
        setSaving(false);
    }, [currentUser, files.boceto, files.fotografias, files.identificacion, form.descripcionTrabajos, form.duracionDias, form.email, form.fechaInicioTrabajo, form.generaraRuido, form.lote, form.nombreCompleto, form.nombreContratista, form.responsableTrabajo, form.telefonoPrincipal, form.tipoTrabajo, fraccionamientoId, isDayAllowed, onSuccess, userData?.rol, validateStep]);

    return (
        <div className="mudanza-modal-overlay" role="presentation">
            <div className="mudanza-modal" role="dialog" aria-modal="true" aria-labelledby="remodelacionModalTitle">
                <header className="mudanza-modal-header">
                    <div className="mudanza-header-meta">
                        <span className="mudanza-template-badge">Remodelación / Modificación</span>
                        <h2 id="remodelacionModalTitle">Nueva solicitud</h2>
                    </div>
                    <button type="button" className="mudanza-close" onClick={onClose} disabled={saving} aria-label="Cerrar">
                        <FaTimes />
                    </button>
                </header>

                <nav className="mudanza-steps" aria-label="Progreso de la solicitud">
                    {STEP_TITLES.map((title, index) => {
                        const status = index === step ? 'active' : (index < step ? 'complete' : 'pending');
                        return (
                            <div key={title} className={`mudanza-step ${status}`}>
                                <span className="mudanza-step-index">{index + 1}</span>
                                <span className="mudanza-step-title">{title}</span>
                            </div>
                        );
                    })}
                </nav>

                {error && (
                    <div className="mudanza-error" role="alert">{error}</div>
                )}

                <div className="mudanza-body">
                    {step === 0 && (
                        <div className="mudanza-section">
                            <h3>Información del Solicitante</h3>
                            <div className="mudanza-grid">
                                <label className="mudanza-field">
                                    <span>Nombre completo *</span>
                                    <input
                                        type="text"
                                        name="nombreCompleto"
                                        value={form.nombreCompleto}
                                        onChange={handleInputChange}
                                        placeholder="Nombre del solicitante"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Número de lote *</span>
                                    <input
                                        type="text"
                                        name="lote"
                                        value={form.lote}
                                        onChange={handleInputChange}
                                        placeholder="Ej. C-08"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Teléfono *</span>
                                    <input
                                        type="tel"
                                        name="telefonoPrincipal"
                                        value={form.telefonoPrincipal}
                                        onChange={handleInputChange}
                                        placeholder="10 dígitos"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Email *</span>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleInputChange}
                                        placeholder="correo@ejemplo.com"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>¿Quién realizará los trabajos? *</span>
                                    <select
                                        name="responsableTrabajo"
                                        value={form.responsableTrabajo}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {RESPONSABLE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mudanza-field">
                                    <span>Nombre del contratista (si aplica)</span>
                                    <input
                                        type="text"
                                        name="nombreContratista"
                                        value={form.nombreContratista}
                                        onChange={handleInputChange}
                                        placeholder="Nombre o empresa"
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="mudanza-section">
                            <h3>Detalles de los Trabajos</h3>
                            <div className="mudanza-grid">
                                <label className="mudanza-field">
                                    <span>Tipo de trabajo *</span>
                                    <select
                                        name="tipoTrabajo"
                                        value={form.tipoTrabajo}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {TRABAJO_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mudanza-field">
                                    <span>Fecha de inicio *</span>
                                    <input
                                        type="date"
                                        name="fechaInicioTrabajo"
                                        value={form.fechaInicioTrabajo}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>Duración (días) *</span>
                                    <input
                                        type="number"
                                        name="duracionDias"
                                        value={form.duracionDias}
                                        min="1"
                                        onChange={handleInputChange}
                                        placeholder="5"
                                        required
                                    />
                                </label>
                                <label className="mudanza-field">
                                    <span>¿Generará ruido? *</span>
                                    <select
                                        name="generaraRuido"
                                        value={form.generaraRuido}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {RUIDO_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mudanza-field">
                                    <span>Horario de llegada *</span>
                                    <select
                                        name="horarioLlegada"
                                        value={form.horarioLlegada}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Selecciona un horario</option>
                                        {horarioSlots.map((slot) => (
                                            <option key={slot} value={slot}>
                                                {formatTimeLabel(slot)}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mudanza-field mudanza-field--textarea" style={{gridColumn: '1 / -1'}}>
                                    <span>Descripción de los trabajos *</span>
                                    <textarea
                                        name="descripcionTrabajos"
                                        value={form.descripcionTrabajos}
                                        onChange={handleInputChange}
                                        placeholder="Describe los trabajos..."
                                        rows={4}
                                        required
                                    />
                                </label>
                            </div>
                            {allowedDaySummary && (
                                <p className="mudanza-hint">
                                    Días permitidos por administración: {allowedDaySummary}.
                                </p>
                            )}
                            <div className="mudanza-info-tip">
                                Para cambios en fachada se requiere aprobación del comité arquitectónico.
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="mudanza-section">
                            <h3>Documentos requeridos</h3>
                            <div className="mudanza-upload-grid">
                                <div className="mudanza-upload-card required">
                                    <header>
                                        <h4>Identificación oficial *</h4>
                                        {files.identificacion && (
                                            <button type="button" onClick={() => handleRemoveFile('identificacion')} className="mudanza-upload-remove">
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input
                                            type="file"
                                            accept={ACCEPTED_TYPES.join(',')}
                                            onChange={(event) => handleFileSelect(event, 'identificacion')}
                                        />
                                        <FaCloudUploadAlt />
                                        <span>{files.identificacion ? files.identificacion.name : 'Clic para subir archivos'}</span>
                                        <small>PDF, JPG, PNG (máx. 10MB)</small>
                                    </label>
                                </div>

                                <div className="mudanza-upload-card required">
                                    <header>
                                        <h4>Fotografías del área *</h4>
                                        {files.fotografias && (
                                            <button type="button" onClick={() => handleRemoveFile('fotografias')} className="mudanza-upload-remove">
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input
                                            type="file"
                                            accept={ACCEPTED_TYPES.join(',')}
                                            onChange={(event) => handleFileSelect(event, 'fotografias')}
                                        />
                                        <FaCloudUploadAlt />
                                        <span>{files.fotografias ? files.fotografias.name : 'Clic para subir archivos'}</span>
                                        <small>PDF, JPG, PNG (máx. 10MB)</small>
                                    </label>
                                </div>

                                <div className="mudanza-upload-card optional">
                                    <header>
                                        <h4>Boceto o diseño (opcional)</h4>
                                        {files.boceto && (
                                            <button type="button" onClick={() => handleRemoveFile('boceto')} className="mudanza-upload-remove">
                                                Quitar
                                            </button>
                                        )}
                                    </header>
                                    <label className="mudanza-upload-drop">
                                        <input
                                            type="file"
                                            accept={ACCEPTED_TYPES.join(',')}
                                            onChange={(event) => handleFileSelect(event, 'boceto')}
                                        />
                                        <FaCloudUploadAlt />
                                        <span>{files.boceto ? files.boceto.name : 'Clic para subir archivos'}</span>
                                        <small>PDF, JPG, PNG (máx. 10MB)</small>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="mudanza-section">
                            <h3>Confirmar solicitud</h3>
                            <div className="mudanza-summary-grid">
                                <div className="mudanza-summary-block">
                                    <h4>Resumen</h4>
                                    <ul>
                                        <li><strong>Tipo:</strong> {trabajoLabel}</li>
                                        <li><strong>Solicitante:</strong> {form.nombreCompleto || '—'}</li>
                                        <li><strong>Lote:</strong> {form.lote || '—'}</li>
                                        <li><strong>Inicio:</strong> {formatDateForSummary(form.fechaInicioTrabajo)}</li>
                                        <li><strong>Duración:</strong> {form.duracionDias ? `${form.duracionDias} días` : '—'}</li>
                                        <li><strong>Responsable:</strong> {responsableLabel}</li>
                                        <li><strong>Ruido estimado:</strong> {ruidoLabel}</li>
                                        <li><strong>Horario de llegada:</strong> {form.horarioLlegada ? formatTimeLabel(form.horarioLlegada) : '—'}</li>
                                    </ul>
                                </div>
                                <div className="mudanza-summary-block">
                                    <h4>Documentos</h4>
                                    <ul>
                                        <li><FaCheckCircle /> Identificación oficial</li>
                                        <li><FaCheckCircle /> Fotografías del área</li>
                                        <li><FaCheckCircle color={files.boceto ? '#2563eb' : '#94a3b8'} /> Boceto o diseño {files.boceto ? '(adjunto)' : '(opcional)'}</li>
                                    </ul>
                                </div>
                            </div>
                            {form.descripcionTrabajos && (
                                <div className="mudanza-summary-block">
                                    <h4>Descripción</h4>
                                    <p>{form.descripcionTrabajos}</p>
                                </div>
                            )}
                            <label className="mudanza-terms">
                                <input
                                    type="checkbox"
                                    name="aceptaCondiciones"
                                    checked={form.aceptaCondiciones}
                                    onChange={handleInputChange}
                                />
                                <span>Acepto respetar los horarios y mantener limpias las áreas comunes.</span>
                            </label>
                        </div>
                    )}
                </div>

                <footer className="mudanza-footer">
                    <button type="button" className="mudanza-btn ghost" onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <div className="mudanza-footer-actions">
                        {step > 0 && (
                            <button type="button" className="mudanza-btn secondary" onClick={handlePrev} disabled={saving}>
                                <FaArrowLeft /> Anterior
                            </button>
                        )}
                        {step < STEP_TITLES.length - 1 && (
                            <button type="button" className="mudanza-btn primary" onClick={handleNext} disabled={saving}>
                                Siguiente <FaArrowRight />
                            </button>
                        )}
                        {step === STEP_TITLES.length - 1 && (
                            <button type="button" className="mudanza-btn primary" onClick={handleSubmit} disabled={saving}>
                                {saving ? 'Enviando…' : 'Enviar solicitud'}
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        </div>
    );
};

SolicitudRemodelacionModal.propTypes = {
    fraccionamientoId: PropTypes.string,
    currentUser: PropTypes.shape({
        uid: PropTypes.string,
        displayName: PropTypes.string,
        email: PropTypes.string,
    }),
    userData: PropTypes.shape({
        rol: PropTypes.string,
        nombreCompleto: PropTypes.string,
        nombre: PropTypes.string,
        apellidos: PropTypes.string,
        displayName: PropTypes.string,
        numeroCasa: PropTypes.string,
        numeroLote: PropTypes.string,
        lote: PropTypes.string,
        houseLabel: PropTypes.string,
        telefono: PropTypes.string,
        telefonoPrincipal: PropTypes.string,
        celular: PropTypes.string,
        phoneNumber: PropTypes.string,
        correo: PropTypes.string,
        email: PropTypes.string,
        emailPrincipal: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};

export default SolicitudRemodelacionModal;
